/**
 * Member profile editor (v2 — DNKADM-0059; enriched read + typed inputs, DNKADM-0104 U1;
 * Internal / CRM Managed two-tab split, DNKADM-0104 U2).
 *
 * **Ownership split (DNKADM-0104 U2).** The editor is grouped into two MUI sub-tabs by the
 * registry `fieldOwnership` (the field-SET axis): **Internal** holds the `DINK`-owned
 * fields (managed here), **CRM Managed** holds the CRM-owned (non-`DINK`) fields
 * (pass-through). The vendor is generalized to "CRM" — the raw brand NEVER renders
 * (§4.0 / white-label). Internal is the default tab; an empty group shows a neutral empty
 * state. Both tabs share ONE draft and ONE flat write: Save submits every changed writable
 * field across both tabs (the DNKAPI-0137 flat body is unchanged — only the layout groups).
 *
 * A registry-DRIVEN editor whose INPUTS are now driven by the ENRICHED value read
 * (DNKAPI-0195). It reads the fail-closed `useMemberProfileFieldList()` (0058
 * `v2_member_profile_field` registry) for the field SET, labels, and `fieldOwnership`,
 * and `getMemberProfileValues` for each field's enriched `{ value, type, options?,
 * writable }`. It writes the changed, writable subset back via `putMemberProfileValues`
 * as the FLAT `{ values: { fieldKey: bareValue } }` body (DNKAPI-0137 — the enriched
 * object is NEVER sent).
 *
 * **Typed inputs (DNKAPI-0195).** The enriched `type` supersedes the registry
 * `fieldType` for rendering:
 *  - `SINGLE_OPTIONS` → a dropdown (`Select`) populated from `options`;
 *  - `MULTI_OPTIONS` → a multi-select from `options` (the value is a list);
 *  - `NUMBER` → a number input; `DATE` → the repo's native date input; `TEXT` → text.
 * The registry had no option source before, so option fields used to render as text —
 * 0195's `options` fixes exactly that.
 *
 * **Editability (§4.0).** Gated on the VALUE's `writable` (the authoritative per-field
 * read — preferred over the registry `isMemberWritable`): a `writable:false` field is
 * READ-ONLY (rendered plainly, never submitted). The reserved `email` key is always
 * read-only. Only changed writable fields are sent, so a read-only write (400
 * `FIELD_NOT_WRITABLE`) is never triggered from the UI.
 *
 * **White-label (§4.0).** `fieldOwnership` is DATA — the raw brand NEVER renders; the
 * ownership hint renders via `fieldOwnershipLabel`.
 *
 * Fail-closed: a registry OR values load failure shows error + Retry, never a silent
 * empty editor that would let the admin wrongly treat a field as unregistered/editable.
 */
import { useCallback, useId, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon, inkAlpha, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { PickListLoader } from '@/components/PickListLoader';
import { useToast } from '@/components/useToast';
import {
  useMemberProfileFieldList,
  type MemberProfileField,
} from '@/api/memberProfileFields';
import {
  getMemberProfileValues,
  putMemberProfileValues,
  type ProfileFieldValue,
  type ProfileValueInput,
  type ProfileValues,
  type ProfileValuesInput,
} from '@/api/members';
import { fieldOwnershipLabel } from '@/utils/whitelabel';
import {
  MEMBERS_FORBIDDEN_MESSAGE,
  MEMBERS_LOAD_FAILED_MESSAGE,
  toProfileSaveErrorMessage,
} from './membersError';
import { ScreenError } from '@/components/ScreenError';

/** The reserved field key that is always read-only/admin-special (§4.0). */
const EMAIL_FIELD_KEY = 'email';

/**
 * The two ownership sub-tabs (DNKADM-0104 U2), in display order — **Internal first**
 * (the default). The vendor is generalized to "CRM Managed" so the raw brand never
 * renders (§4.0). Each carries its own neutral empty-state copy.
 */
const OWNERSHIP_TABS = [
  { value: 'internal', label: 'Internal', empty: 'No internal fields' },
  { value: 'crm', label: 'CRM Managed', empty: 'No CRM-managed fields' },
] as const;

type OwnershipTab = (typeof OWNERSHIP_TABS)[number]['value'];

/**
 * Partition the registry fields into the two ownership groups: `DINK`-owned → **Internal**,
 * everything else (the CRM-owned pass-through) → **CRM Managed**. Comparing against the
 * safe `'DINK'` literal (never the raw `'GHL'` brand token) keeps the white-label guard
 * green — a non-`DINK` ownership degrades into CRM Managed, matching the registry's own
 * fail-safe default.
 */
function partitionByOwnership(fields: MemberProfileField[]): Record<OwnershipTab, MemberProfileField[]> {
  const internal: MemberProfileField[] = [];
  const crm: MemberProfileField[] = [];
  for (const field of fields) {
    if (field.fieldOwnership === 'DINK') internal.push(field);
    else crm.push(field);
  }
  return { internal, crm };
}

/**
 * The safe fallback for a registry field the enriched read didn't return: a read-only
 * empty `TEXT` field (fail-safe — never wrongly editable).
 */
const FALLBACK_VALUE: ProfileFieldValue = { value: null, type: 'TEXT', writable: false };

/**
 * Decide whether the admin may edit a field, preferring the VALUE's `writable` (the
 * authoritative per-field read) over the registry `isMemberWritable`. The reserved
 * `email` key is always read-only.
 */
function isFieldEditable(field: MemberProfileField, value: ProfileFieldValue): boolean {
  if (field.fieldKey.trim().toLowerCase() === EMAIL_FIELD_KEY) return false;
  return value.writable === true;
}

/** A single editor value: a string (text/number/date/single-select) or a list (multi-select). */
type EditorValue = string | string[];

/** Coerce an enriched value to the editor's string/list representation. */
function toEditorValue(value: ProfileFieldValue): EditorValue {
  if (value.type === 'MULTI_OPTIONS') {
    return Array.isArray(value.value) ? value.value : [];
  }
  if (value.value === null || value.value === undefined) return '';
  return String(value.value);
}

/** Coerce an editor value back to the FLAT wire value for its type. */
function toWireValue(value: ProfileFieldValue, editor: EditorValue): ProfileValueInput {
  if (value.type === 'MULTI_OPTIONS') {
    return Array.isArray(editor) ? editor : [];
  }
  const text = String(editor).trim();
  if (text === '') return null;
  if (value.type === 'NUMBER') {
    const n = Number(text);
    return Number.isFinite(n) ? n : text;
  }
  return text;
}

/** Compare a next wire value against an original enriched value (list-aware). */
function sameValue(next: ProfileValueInput, original: ProfileFieldValue['value']): boolean {
  if (Array.isArray(next) && Array.isArray(original)) {
    return next.length === original.length && next.every((v, i) => v === original[i]);
  }
  return next === original;
}

/**
 * A stable signature of the loaded values (by key + value). Used as the `ProfileForm`
 * key so the draft RESEEDS whenever the values change — i.e. after a save the form
 * refreshes from the re-read enriched map (incl. any server normalization). While the
 * user types, `initialValues` is unchanged, so the signature is stable and edits persist.
 */
function valuesSignature(values: ProfileValues): string {
  return Object.keys(values)
    .sort()
    .map((key) => `${key}=${JSON.stringify(values[key]?.value ?? null)}`)
    .join('|');
}

/** Human display for a read-only value (list joined, boolean as Yes/No, unset as em dash). */
function readOnlyText(value: ProfileFieldValue['value']): string {
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

export function MemberProfileTab({
  contactId,
  onSaved,
}: {
  contactId: string;
  /**
   * Fired after a successful save, in addition to the tab's own values
   * refetch (DNKADM-0304 U3) — the caller reloads the member-detail header so
   * a registry field that feeds it (writable name/phone) reflects without a
   * manual refresh.
   */
  onSaved?: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const registry = useMemberProfileFieldList();
  const values = useResource(
    useCallback(() => getMemberProfileValues(contactId), [contactId]),
    [contactId],
  );

  return (
    <Paper elevation={1} sx={{ p: '20px 24px', maxWidth: 720 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 500, mb: 0.5 }}>Profile</Typography>
      <Typography variant="body2" sx={{ color: t.text2, mb: 2 }}>
        Editable fields are managed here or passed through to the contact system. Read-only
        fields are shown for reference.
      </Typography>

      {/* Fail-closed: the registry must load completely before the editor renders — a
          registry failure shows error + Retry, never a silent empty editor. */}
      <PickListLoader list={registry} label="the profile field registry">
        {(fields) => (
          <ProfileEditor
            contactId={contactId}
            fields={fields}
            valuesStatus={values.status}
            valuesError={values.error}
            initialValues={values.data}
            onSaved={() => {
              values.refetch();
              onSaved?.();
            }}
          />
        )}
      </PickListLoader>
    </Paper>
  );
}

function ProfileEditor({
  contactId,
  fields,
  valuesStatus,
  valuesError,
  initialValues,
  onSaved,
}: {
  contactId: string;
  fields: MemberProfileField[];
  valuesStatus: 'loading' | 'ready' | 'error';
  valuesError: unknown;
  initialValues: ProfileValues | null;
  onSaved: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  if (valuesStatus === 'loading') {
    return (
      <Stack sx={{ gap: 1.5 }} role="status" aria-label="Loading profile values">
        {Array.from({ length: Math.max(2, Math.min(fields.length, 4)) }).map((_, i) => (
          <Skeleton key={`pv-${i}`} variant="rounded" height={48} />
        ))}
      </Stack>
    );
  }
  if (valuesStatus === 'error') {
    return (
      <ScreenError
        error={valuesError}
        what="the profile values"
        forbiddenMessage={MEMBERS_FORBIDDEN_MESSAGE}
        retryMessage={MEMBERS_LOAD_FAILED_MESSAGE}
        preferServerMessage
      />
    );
  }
  if (fields.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Icon name="badge" sx={{ fontSize: 38, color: t.lineStrong }} />
        <Typography sx={{ fontSize: 14, fontWeight: 500, mt: 1 }}>
          No profile fields configured
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 360, mx: 'auto' }}>
          Member profile fields are configured under Member profile fields. Once added, they
          appear here for editing.
        </Typography>
      </Box>
    );
  }
  const values = initialValues ?? {};
  return (
    <ProfileForm
      // Reseed the draft when the values change (post-save refresh from the enriched read).
      key={valuesSignature(values)}
      contactId={contactId}
      fields={fields}
      initialValues={values}
      onSaved={onSaved}
    />
  );
}

function ProfileForm({
  contactId,
  fields,
  initialValues,
  onSaved,
}: {
  contactId: string;
  fields: MemberProfileField[];
  initialValues: ProfileValues;
  onSaved: () => void;
}) {
  const theme = useTheme();
  const tok = resolveTokens(theme);
  const toast = useToast();

  // Seed the editable-field draft from the loaded enriched values. Read-only fields are
  // shown from the loaded values directly and never enter the draft (never submitted).
  const seed = useMemo<Record<string, EditorValue>>(() => {
    const out: Record<string, EditorValue> = {};
    for (const field of fields) {
      const value = initialValues[field.fieldKey] ?? FALLBACK_VALUE;
      if (isFieldEditable(field, value)) {
        out[field.fieldKey] = toEditorValue(value);
      }
    }
    return out;
  }, [fields, initialValues]);

  const [draft, setDraft] = useState<Record<string, EditorValue>>(seed);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Group the fields by registry ownership for the two-tab split (U2). The draft + save
  // still span ALL fields (one flat write across both tabs); only the rendered SET is
  // filtered per tab.
  const groups = useMemo(() => partitionByOwnership(fields), [fields]);
  const [activeTab, setActiveTab] = useState<OwnershipTab>('internal');
  const activeFields = groups[activeTab];

  function setValue(key: string, value: EditorValue) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  // Only the editable fields whose value changed are dirty (and submitted), as FLAT
  // bare values (DNKAPI-0137 — never the enriched object).
  const changed = useMemo(() => {
    const out: ProfileValuesInput = {};
    for (const field of fields) {
      const value = initialValues[field.fieldKey] ?? FALLBACK_VALUE;
      if (!isFieldEditable(field, value)) continue;
      const next = toWireValue(value, draft[field.fieldKey] ?? toEditorValue(value));
      if (!sameValue(next, value.value)) out[field.fieldKey] = next;
    }
    return out;
  }, [fields, draft, initialValues]);

  const hasChanges = Object.keys(changed).length > 0;

  async function handleSave() {
    if (!hasChanges || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      // The PUT response is the re-read enriched map; refresh the form from it.
      await putMemberProfileValues(contactId, changed);
      toast('Profile updated.');
      onSaved();
    } catch (err) {
      setError(toProfileSaveErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Stack sx={{ gap: 2 }}>
      <Tabs
        value={activeTab}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        onChange={(_e, value: OwnershipTab) => setActiveTab(value)}
        aria-label="Profile field ownership"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTabs-indicator': { bgcolor: tok.brand },
          '& .Mui-selected': { color: `${tok.brand} !important` },
        }}
      >
        {OWNERSHIP_TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} id={`profile-tab-${t.value}`} />
        ))}
      </Tabs>

      {/* One panel — the active tab's field SUBSET. The other tab's rows are unmounted
          but their draft values persist (the draft spans all fields), so a save still
          submits every changed writable field across both tabs. */}
      <Box role="tabpanel" aria-labelledby={`profile-tab-${activeTab}`}>
        {activeFields.length === 0 ? (
          <Typography variant="body2" sx={{ color: tok.text2, py: 2 }}>
            {OWNERSHIP_TABS.find((t) => t.value === activeTab)?.empty}
          </Typography>
        ) : (
          <Stack sx={{ gap: 2 }}>
            {activeFields.map((field) => {
              const value = initialValues[field.fieldKey] ?? FALLBACK_VALUE;
              return (
                <ProfileFieldRow
                  key={field.id}
                  field={field}
                  value={value}
                  editable={isFieldEditable(field, value)}
                  draftValue={draft[field.fieldKey]}
                  disabled={submitting}
                  onChange={(v) => setValue(field.fieldKey, v)}
                />
              );
            })}
          </Stack>
        )}
      </Box>

      {error && (
        <Alert severity="error" role="alert">
          {error}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          startIcon={<Icon name="check" />}
          onClick={() => void handleSave()}
          disabled={!hasChanges || submitting}
        >
          {submitting ? 'Saving…' : 'Save profile'}
        </Button>
      </Box>
    </Stack>
  );
}

function ProfileFieldRow({
  field,
  value,
  editable,
  draftValue,
  disabled,
  onChange,
}: {
  field: MemberProfileField;
  value: ProfileFieldValue;
  editable: boolean;
  draftValue: EditorValue | undefined;
  disabled: boolean;
  onChange: (value: EditorValue) => void;
}) {
  const theme = useTheme();
  const labelId = useId();
  const ownershipHint = fieldOwnershipLabel(field.fieldOwnership);

  // Read-only: render the loaded value plainly (never submitted).
  if (!editable) {
    return (
      <Box>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{field.fieldLabel}</Typography>
          <Chip
            size="small"
            icon={<Icon name="lock" sx={{ fontSize: 14 }} />}
            label="Read-only"
            sx={{ height: 22 }}
          />
        </Stack>
        <Typography sx={{ fontSize: 14 }}>{readOnlyText(value.value)}</Typography>
        <Typography sx={{ fontSize: 11.5, color: inkAlpha(theme, 0.5), mt: 0.25 }}>
          {ownershipHint}
        </Typography>
      </Box>
    );
  }

  const options = value.options ?? [];

  // Single-choice: a dropdown populated from the enriched `options` (0195).
  if (value.type === 'SINGLE_OPTIONS') {
    const current = typeof draftValue === 'string' ? draftValue : '';
    // Keep the current value selectable even if it has drifted off the option list.
    const menuOptions = current && !options.includes(current) ? [current, ...options] : options;
    return (
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel id={labelId}>{field.fieldLabel}</InputLabel>
        <Select
          labelId={labelId}
          label={field.fieldLabel}
          value={current}
          onChange={(e) => onChange(e.target.value)}
          slotProps={{ input: { 'aria-label': field.fieldLabel } }}
        >
          <MenuItem value="">
            <em>—</em>
          </MenuItem>
          {menuOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        <OwnershipHint>{ownershipHint}</OwnershipHint>
      </FormControl>
    );
  }

  // Multi-choice: a multi-select from the enriched `options` (the value is a list).
  if (value.type === 'MULTI_OPTIONS') {
    const current = Array.isArray(draftValue) ? draftValue : [];
    const extra = current.filter((v) => !options.includes(v));
    const menuOptions = [...extra, ...options];
    return (
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel id={labelId}>{field.fieldLabel}</InputLabel>
        <Select
          labelId={labelId}
          label={field.fieldLabel}
          multiple
          value={current}
          onChange={(e) =>
            onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)
          }
          renderValue={(selected) => (selected as string[]).join(', ')}
          slotProps={{ input: { 'aria-label': field.fieldLabel } }}
        >
          {menuOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        <OwnershipHint>{ownershipHint}</OwnershipHint>
      </FormControl>
    );
  }

  // Text / number / date: a plain input typed from the enriched value.
  const inputType = value.type === 'NUMBER' ? 'number' : value.type === 'DATE' ? 'date' : 'text';
  return (
    <TextField
      label={field.fieldLabel}
      type={inputType}
      fullWidth
      size="small"
      value={typeof draftValue === 'string' ? draftValue : ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      helperText={ownershipHint}
      {...(value.type === 'DATE' ? { slotProps: { inputLabel: { shrink: true } } } : {})}
    />
  );
}

/** The brand-neutral ownership hint shown under a Select input. */
function OwnershipHint({ children }: { children: ReactNode }) {
  const theme = useTheme();
  return (
    <Typography sx={{ fontSize: 11.5, color: inkAlpha(theme, 0.5), mt: 0.5, ml: 1.75 }}>
      {children}
    </Typography>
  );
}
